import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchAllEmployees } from "../../store/allocation/employeeSlice";
import {
  fetchLeaveBalances,
  fetchLeaveTypes,
  fetchPunchReminderBypass,
  markHrAttendance,
  setHrLeaveBalance,
  setPunchReminderBypass,
} from "../../store/hr/hrSlice";
import { canManageAttendance, canViewAttendanceReport } from "../../utils/employeeRoles";
import AttendanceReportDownloads from "../../components/employee/AttendanceReportDownloads";
import { useHrAccess } from "../../hooks/useHrAccess";
import {
  canAdjustLeaveBalanceOf,
  canManageLeaveBalances,
} from "../../utils/leaveHierarchy";
import { codeOf, remainingOf } from "../../utils/leavePolicy";
import api from "../../utils/api";
import { HrAttendance, HrLeaveType } from "../../types/HrTypes";
import PunchIntegration from "../hr/PunchIntegration";
import { DASHBOARD } from "../../components/employee-dashboard/dashboardTheme";
import {
  AdminPrimaryButton,
  AdminSoftSelect,
} from "../../components/admin/AdminPageShell";
import {
  CustomDatePicker,
  CustomMonthPicker,
} from "../../components/common/DatePickers";
import { AttendanceMonthCalendar } from "../../components/common/AttendanceMonthCalendar";
import { attendanceMarkedHow } from "../../utils/attendanceDisplay";

type Tab = "live" | "daily" | "month";
type StatusFilter =
  | "all"
  | "in"
  | "out"
  | "no_punch"
  | "late"
  | "early"
  | "present"
  | "absent"
  | "half_day"
  | "on_leave"
  | "unpaid"
  | "holiday"
  | "weekend";

type DraftMark = {
  emp_id: string;
  attendance_date: string;
  status: string;
  leave_type_code: string;
  notes: string;
  emp_name?: string;
};

type BalanceDraft = {
  pl: string;
  cl: string;
  hol: string;
  ul: string;
};

type LeaveTypeIds = Partial<Record<"PL" | "CL" | "HOL" | "UL", number>>;

function emptyBalanceDraft(): BalanceDraft {
  return { pl: "0", cl: "0", hol: "0", ul: "0" };
}

function nudge(value: string, delta: number, min = 0) {
  const next = Number((Number(value || 0) + delta).toFixed(2));
  return String(Math.max(min, next));
}

function BalanceStepper({
  label,
  hint,
  value,
  onChange,
  step = 0.5,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (next: string) => void;
  step?: number;
}) {
  return (
    <div className="rounded-xl border border-[#E8E8E4] bg-[#FDFBF2] px-3 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold text-[#4A4A48]">{label}</p>
          <p className="text-[11px] text-[#8A8A85]">{hint}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#DDDDD8] bg-white text-sm font-semibold text-[#345952]"
            onClick={() => onChange(nudge(value, -step))}
            aria-label={`Decrease ${label}`}
          >
            −
          </button>
          <input
            type="number"
            min={0}
            step={step}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-16 rounded-lg border border-[#E8E8E4] bg-white px-2 py-1.5 text-center text-sm font-semibold text-[#2C2C2A] outline-none focus:ring-2 focus:ring-[#345952]/20"
          />
          <button
            type="button"
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#DDDDD8] bg-white text-sm font-semibold text-[#345952]"
            onClick={() => onChange(nudge(value, step))}
            aria-label={`Increase ${label}`}
          >
            +
          </button>
        </div>
      </div>
    </div>
  );
}

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

function currentMonthValue() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function rangeForMonth(yearMonth: string) {
  const [y, m] = yearMonth.split("-").map(Number);
  const from = `${yearMonth}-01`;
  const lastDay = new Date(y, m, 0).getDate();
  const monthEnd = `${yearMonth}-${String(lastDay).padStart(2, "0")}`;
  const today = todayIso();
  return { from, to: monthEnd > today ? today : monthEnd };
}

function prettyDate(value?: string | null) {
  if (!value) return "—";
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return value;
  return new Date(y, m - 1, d).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function clock(value: string | null | undefined) {
  if (!value) return "—";
  return new Date(value).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatWorked(minutes: number | null | undefined) {
  const total = Math.max(0, Number(minutes) || 0);
  if (total === 0) return "—";
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function initials(name?: string) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  return ((parts[0][0] || "") + (parts[1]?.[0] || "")).toUpperCase();
}

function statusLabel(status: string, leaveType?: { code?: string; name?: string } | null) {
  const key = String(status || "").toLowerCase();
  const code = String(leaveType?.code || "").toUpperCase();
  if (key === "unpaid" || (key === "on_leave" && code === "UL")) return "Unpaid";
  if (key === "half_day") return code ? `Half day (${code})` : "Half day";
  if (key === "on_leave") return leaveType?.name || code || "Leave";
  if (key === "wfh") return "WFH";
  if (key === "present") return "Present";
  if (key === "absent") return "Absent";
  if (key === "weekend") return "Week off";
  if (key === "holiday") return code === "HOL" ? "Holiday (quota)" : "Holiday";
  return status.split("_").join(" ");
}

function statusTone(status: string) {
  const key = String(status || "").toLowerCase();
  if (key === "present" || key === "wfh") return "bg-[#E8F3EF] text-[#345952]";
  if (key === "on_leave") return "bg-[#F3EEE8] text-[#8A6A48]";
  if (key === "unpaid") return "bg-[#F8EAEA] text-[#9B3B3B]";
  if (key === "absent") return "bg-[#F8EAEA] text-[#9B3B3B]";
  if (key === "half_day") return "bg-[#EAF2F8] text-[#3B6B9B]";
  if (key === "holiday") return "bg-[#F0ECF8] text-[#5B4A8A]";
  if (key === "weekend") return "bg-[#F5F5F0] text-[#8A8A85]";
  return "bg-[#F5F5F0] text-[#4A4A48]";
}

function liveBucket(row: HrAttendance | undefined) {
  if (!row || row.status === "absent" || (!row.punch_in_at && row.status !== "present")) {
    return "no_punch";
  }
  if (row.status === "present" || row.status === "wfh") {
    if (row.punch_in_at && !row.punch_out_at) return "in";
    if (row.punch_out_at) return "out";
    return "in";
  }
  return "other";
}

type MonthAgg = {
  emp_id: number;
  name: string;
  role: string;
  shift: string;
  present: number;
  absent: number;
  half_day: number;
  weekend: number;
  holiday: number;
  paid_leave: number;
  unpaid: number;
  late: number;
  early: number;
};

function Chip({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean;
  label: string;
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-xl border px-3 py-2 text-left text-xs font-medium transition ${
        active
          ? "border-[#345952] bg-[#345952] text-white"
          : "border-[#E8E8E4] bg-white text-[#4A4A48] hover:border-[#345952]/40"
      }`}
    >
      {label} <span className={active ? "text-white/80" : "text-[#8A8A85]"}>({count})</span>
    </button>
  );
}

function StatusPill({
  active,
  label,
  tone,
  onClick,
}: {
  active: boolean;
  label: string;
  tone: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide ${
        active ? "ring-2 ring-[#345952] ring-offset-1 " : ""
      }${tone}`}
    >
      {label}
    </button>
  );
}

const AttendanceReport = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { employee } = useAppSelector((state) => state.allocationAuth);
  const { allEmployees } = useAppSelector((state) => state.employee);
  const { isHrAdmin, canEditNotebook, canMarkAttendance, empId } = useHrAccess();
  const canViewTeam = canViewAttendanceReport(user?.role, employee?.role);
  const canDownloadReports = canManageAttendance(user?.role, employee?.role);
  const balanceActor = {
    portalRole: user?.role,
    empRole: employee?.role,
    empId,
  };
  const managesBalances = canManageLeaveBalances(balanceActor);

  const verifiedEmployees = useMemo(
    () => allEmployees.filter((row) => row.verified),
    [allEmployees],
  );

  const canEditBalancesFor = (targetEmpId: number) => {
    if (!managesBalances) return false;
    const row = verifiedEmployees.find((e) => e.emp_id === targetEmpId);
    return canAdjustLeaveBalanceOf(balanceActor, {
      role: row?.role,
      emp_id: targetEmpId,
    });
  };

  const [tab, setTab] = useState<Tab>(canViewTeam ? "daily" : "month");
  const [day, setDay] = useState(todayIso());
  const [month, setMonth] = useState(currentMonthValue());
  const [monthEmpId, setMonthEmpId] = useState<number | null>(null);
  const monthCalendarRef = useRef<HTMLDivElement>(null);
  const [shiftFilter, setShiftFilter] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [attendance, setAttendance] = useState<HrAttendance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [drawer, setDrawer] = useState<DraftMark | null>(null);
  const [balanceDraft, setBalanceDraft] = useState<BalanceDraft>(emptyBalanceDraft);
  const [balanceBaseline, setBalanceBaseline] = useState<BalanceDraft>(emptyBalanceDraft);
  const [leaveTypeIds, setLeaveTypeIds] = useState<LeaveTypeIds>({});
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingBalances, setSavingBalances] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [saveErr, setSaveErr] = useState("");
  const [adminTools, setAdminTools] = useState(false);
  const [bypass, setBypass] = useState<{
    active: boolean;
    until: string | null;
    by: string | null;
  } | null>(null);
  const [bypassBusy, setBypassBusy] = useState(false);

  const canEditBalances = Boolean(
    drawer && canEditBalancesFor(Number(drawer.emp_id)),
  );

  const shifts = useMemo(() => {
    const set = new Set<string>();
    for (const e of verifiedEmployees) {
      if (e.shift) set.add(e.shift);
    }
    return [...set].sort();
  }, [verifiedEmployees]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const isMonth = tab === "month";
      const range = isMonth
        ? rangeForMonth(month)
        : { from: day, to: day };
      const res = await api.get("/allocation-tool/employee/attendance", {
        params: {
          from: range.from,
          to: range.to,
          month: isMonth ? month : day.slice(0, 7),
          period: isMonth ? "monthly" : "daily",
          emp_id: canViewTeam ? undefined : employee?.emp_id,
        },
      });
      setAttendance(res.data.data.attendance || []);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Could not load attendance";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canViewTeam || canMarkAttendance) dispatch(fetchAllEmployees());
  }, [dispatch, canViewTeam, canMarkAttendance]);

  useEffect(() => {
    if (!canViewTeam && tab !== "month" && tab !== "daily") setTab("month");
  }, [canViewTeam, tab]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, day, month, canViewTeam, employee?.emp_id]);

  useEffect(() => {
    if (!canMarkAttendance) return;
    let active = true;
    dispatch(fetchPunchReminderBypass()).then((result) => {
      if (active && fetchPunchReminderBypass.fulfilled.match(result)) {
        setBypass(result.payload);
      }
    });
    return () => {
      active = false;
    };
  }, [canMarkAttendance, dispatch]);

  const attendanceByEmp = useMemo(() => {
    const map = new Map<number, HrAttendance>();
    for (const row of attendance) map.set(row.emp_id, row);
    return map;
  }, [attendance]);

  const calendarEmpId = canViewTeam ? monthEmpId : employee?.emp_id ?? null;
  const calendarRows = useMemo(() => {
    if (!calendarEmpId) return [];
    return attendance.filter((row) => row.emp_id === calendarEmpId);
  }, [attendance, calendarEmpId]);

  const staffRows = useMemo(() => {
    const source = canViewTeam
      ? verifiedEmployees
      : employee
        ? verifiedEmployees.filter((e) => e.emp_id === employee.emp_id)
        : [];
    return source
      .filter((e) => (!shiftFilter ? true : e.shift === shiftFilter))
      .filter((e) => {
        if (!search.trim()) return true;
        return e.emp_name.toLowerCase().includes(search.trim().toLowerCase());
      })
      .map((e) => ({
        emp: e,
        row: attendanceByEmp.get(e.emp_id),
      }));
  }, [canViewTeam, verifiedEmployees, employee, shiftFilter, search, attendanceByEmp]);

  const liveCounts = useMemo(() => {
    let inn = 0;
    let out = 0;
    let noPunch = 0;
    let late = 0;
    let early = 0;
    for (const { row } of staffRows) {
      const bucket = liveBucket(row);
      if (bucket === "in") inn += 1;
      else if (bucket === "out") out += 1;
      else if (bucket === "no_punch") noPunch += 1;
      if (row?.late_minutes) late += 1;
      if (row?.early_leave_minutes) early += 1;
    }
    return { inn, out, noPunch, late, early };
  }, [staffRows]);

  const dailyCounts = useMemo(() => {
    const tally = {
      present: 0,
      absent: 0,
      half_day: 0,
      on_leave: 0,
      unpaid: 0,
      holiday: 0,
      weekend: 0,
    };
    for (const { row } of staffRows) {
      const key = String(row?.status || "absent").toLowerCase();
      if (key === "present" || key === "wfh") tally.present += 1;
      else if (key === "absent" || !row) tally.absent += 1;
      else if (key === "half_day") tally.half_day += 1;
      else if (key === "on_leave") tally.on_leave += 1;
      else if (key === "unpaid") tally.unpaid += 1;
      else if (key === "holiday") tally.holiday += 1;
      else if (key === "weekend") tally.weekend += 1;
      else tally.absent += 1;
    }
    return tally;
  }, [staffRows]);

  const filteredStaff = useMemo(() => {
    return staffRows.filter(({ row }) => {
      if (statusFilter === "all") return true;
      if (tab === "live") {
        const bucket = liveBucket(row);
        if (statusFilter === "in") return bucket === "in";
        if (statusFilter === "out") return bucket === "out";
        if (statusFilter === "no_punch") return bucket === "no_punch";
        if (statusFilter === "late") return Boolean(row?.late_minutes);
        if (statusFilter === "early") return Boolean(row?.early_leave_minutes);
      }
      const key = String(row?.status || "absent").toLowerCase();
      if (statusFilter === "present") return key === "present" || key === "wfh";
      if (statusFilter === "absent") return key === "absent" || !row;
      return key === statusFilter;
    });
  }, [staffRows, statusFilter, tab]);

  const monthAgg = useMemo(() => {
    const map = new Map<number, MonthAgg>();
    const ensure = (empId: number, name: string, role: string, shift: string) => {
      if (!map.has(empId)) {
        map.set(empId, {
          emp_id: empId,
          name,
          role,
          shift,
          present: 0,
          absent: 0,
          half_day: 0,
          weekend: 0,
          holiday: 0,
          paid_leave: 0,
          unpaid: 0,
          late: 0,
          early: 0,
        });
      }
      return map.get(empId)!;
    };

    for (const e of canViewTeam
      ? verifiedEmployees.filter((row) => (!shiftFilter ? true : row.shift === shiftFilter))
      : employee
        ? [{ emp_id: employee.emp_id, emp_name: employee.emp_name, role: employee.role, shift: employee.shift }]
        : []) {
      ensure(e.emp_id, e.emp_name, e.role || "", e.shift || "");
    }

    for (const row of attendance) {
      const emp = verifiedEmployees.find((e) => e.emp_id === row.emp_id);
      if (shiftFilter && emp && emp.shift !== shiftFilter) continue;
      if (
        search.trim() &&
        !(emp?.emp_name || row.Employee?.emp_name || "")
          .toLowerCase()
          .includes(search.trim().toLowerCase())
      ) {
        continue;
      }
      const agg = ensure(
        row.emp_id,
        emp?.emp_name || row.Employee?.emp_name || String(row.emp_id),
        emp?.role || row.Employee?.role || "",
        emp?.shift || row.Employee?.shift || "",
      );
      const key = String(row.status || "").toLowerCase();
      const code = String(row.leaveType?.code || "").toUpperCase();
      if (key === "present" || key === "wfh") agg.present += 1;
      else if (key === "absent") agg.absent += 1;
      else if (key === "half_day") agg.half_day += 1;
      else if (key === "weekend") agg.weekend += 1;
      else if (key === "holiday") agg.holiday += 1;
      else if (key === "unpaid" || code === "UL") agg.unpaid += 1;
      else if (key === "on_leave") agg.paid_leave += 1;
      if (row.late_minutes) agg.late += 1;
      if (row.early_leave_minutes) agg.early += 1;
    }

    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [attendance, verifiedEmployees, canViewTeam, employee, shiftFilter, search]);

  useEffect(() => {
    if (!canViewTeam || monthEmpId != null) return;
    if (employee?.emp_id) {
      setMonthEmpId(employee.emp_id);
      return;
    }
    if (monthAgg[0]) setMonthEmpId(monthAgg[0].emp_id);
  }, [canViewTeam, monthEmpId, monthAgg, employee?.emp_id]);

  const calendarName =
    monthAgg.find((row) => row.emp_id === calendarEmpId)?.name ||
    verifiedEmployees.find((row) => row.emp_id === calendarEmpId)?.emp_name ||
    "";

  const loadDrawerBalances = async (targetEmpId: number) => {
    if (!canEditBalancesFor(targetEmpId)) return;
    setBalancesLoading(true);
    try {
      const year = new Date().getFullYear();
      const [typesResult, balancesResult] = await Promise.all([
        dispatch(fetchLeaveTypes()),
        dispatch(fetchLeaveBalances({ emp_id: targetEmpId, year })),
      ]);
      const types: HrLeaveType[] = fetchLeaveTypes.fulfilled.match(typesResult)
        ? typesResult.payload
        : [];
      const ids: LeaveTypeIds = {};
      for (const t of types) {
        const code = codeOf(t) as keyof LeaveTypeIds;
        if (code === "PL" || code === "CL" || code === "HOL" || code === "UL") {
          ids[code] = t.leave_type_id;
        }
      }
      setLeaveTypeIds(ids);

      const balances = fetchLeaveBalances.fulfilled.match(balancesResult)
        ? balancesResult.payload
        : [];
      const byCode = (code: string) =>
        balances.find((b) => codeOf(b.leaveType) === code);
      const pl = byCode("PL");
      const cl = byCode("CL");
      const hol = byCode("HOL");
      const ul = byCode("UL");
      const next = {
        pl: String(pl ? remainingOf(pl) : 0),
        cl: String(cl ? remainingOf(cl) : 0),
        hol: String(hol ? remainingOf(hol) : 0),
        ul: String(ul ? Number(ul.used) : 0),
      };
      setBalanceDraft(next);
      setBalanceBaseline(next);
    } finally {
      setBalancesLoading(false);
    }
  };

  const openDrawer = (targetEmpId: number, empName: string, row?: HrAttendance) => {
    if (!canMarkAttendance) return;
    const code = String(row?.leaveType?.code || "").toUpperCase();
    setDrawer({
      emp_id: String(targetEmpId),
      emp_name: empName,
      attendance_date: day,
      status: row?.status || "present",
      leave_type_code:
        code === "PL" || code === "CL" || code === "HOL" || code === "UL"
          ? code
          : row?.status === "half_day"
            ? "CL"
            : row?.status === "unpaid"
              ? "UL"
              : "PL",
      notes: row?.notes || "",
    });
    setBalanceDraft(emptyBalanceDraft());
    setSaveMsg("");
    setSaveErr("");
    void loadDrawerBalances(targetEmpId);
  };

  const leaveTypePayload = (draft: DraftMark) => {
    if (draft.status === "unpaid") return "UL";
    if (draft.status === "on_leave" || draft.status === "half_day") return draft.leave_type_code;
    if (draft.status === "holiday" && draft.leave_type_code === "HOL") return "HOL";
    return undefined;
  };

  const saveDrawer = async () => {
    if (!drawer) return;
    if (drawer.status === "on_leave" && !["PL", "CL", "HOL"].includes(drawer.leave_type_code)) {
      setSaveErr("Choose PL, CL, or Holiday");
      return;
    }
    if (drawer.status === "half_day" && !["PL", "CL", "UL"].includes(drawer.leave_type_code)) {
      setSaveErr("Choose PL, CL, or Unpaid for half day");
      return;
    }
    setSaving(true);
    setSaveErr("");
    setSaveMsg("");
    try {
      await dispatch(
        markHrAttendance({
          emp_id: Number(drawer.emp_id),
          attendance_date: drawer.attendance_date,
          status: drawer.status,
          notes: drawer.notes,
          leave_type_code: leaveTypePayload(drawer),
        }),
      ).unwrap();
      setSaveMsg("Attendance saved. Leave balances updated if the status changed.");
      await load();
      if (canEditBalancesFor(Number(drawer.emp_id))) {
        await loadDrawerBalances(Number(drawer.emp_id));
      }
    } catch (err: unknown) {
      setSaveErr(typeof err === "string" ? err : "Failed to save attendance");
    } finally {
      setSaving(false);
    }
  };

  const saveBalances = async () => {
    if (!drawer || !canEditBalancesFor(Number(drawer.emp_id))) return;
    const emp = Number(drawer.emp_id);
    const year = new Date().getFullYear();
    const pl = Number(balanceDraft.pl);
    const cl = Number(balanceDraft.cl);
    const hol = Number(balanceDraft.hol);
    const ul = Number(balanceDraft.ul);
    if ([pl, cl, hol, ul].some((n) => Number.isNaN(n) || n < 0)) {
      setSaveErr("Leave balances must be zero or more");
      return;
    }
    if (!leaveTypeIds.PL || !leaveTypeIds.CL || !leaveTypeIds.HOL || !leaveTypeIds.UL) {
      setSaveErr("Leave types are not ready yet. Try again in a moment.");
      return;
    }
    setSavingBalances(true);
    setSaveErr("");
    setSaveMsg("");
    try {
      const jobs: Promise<unknown>[] = [];
      const changed = (a: number, b: string) =>
        Number(a.toFixed(2)) !== Number(Number(b || 0).toFixed(2));
      if (changed(pl, balanceBaseline.pl)) {
        jobs.push(
          dispatch(
            setHrLeaveBalance({
              emp_id: emp,
              leave_type_id: leaveTypeIds.PL,
              remaining: pl,
              year,
              source: "attendance",
            }),
          ).unwrap(),
        );
      }
      if (changed(cl, balanceBaseline.cl)) {
        jobs.push(
          dispatch(
            setHrLeaveBalance({
              emp_id: emp,
              leave_type_id: leaveTypeIds.CL,
              remaining: cl,
              year,
              source: "attendance",
            }),
          ).unwrap(),
        );
      }
      if (changed(hol, balanceBaseline.hol)) {
        jobs.push(
          dispatch(
            setHrLeaveBalance({
              emp_id: emp,
              leave_type_id: leaveTypeIds.HOL,
              remaining: hol,
              year,
              source: "attendance",
            }),
          ).unwrap(),
        );
      }
      if (changed(ul, balanceBaseline.ul)) {
        jobs.push(
          dispatch(
            setHrLeaveBalance({
              emp_id: emp,
              leave_type_id: leaveTypeIds.UL,
              used: ul,
              year,
              source: "attendance",
            }),
          ).unwrap(),
        );
      }
      if (!jobs.length) {
        setSaveMsg("No leave balance changes to save.");
        return;
      }
      await Promise.all(jobs);
      setSaveMsg("Leave balances saved.");
      await loadDrawerBalances(emp);
    } catch (err: unknown) {
      setSaveErr(typeof err === "string" ? err : "Failed to save leave balances");
    } finally {
      setSavingBalances(false);
    }
  };

  const toggleBypass = async (enabled: boolean) => {
    setBypassBusy(true);
    try {
      const result = await dispatch(setPunchReminderBypass(enabled));
      if (setPunchReminderBypass.fulfilled.match(result)) setBypass(result.payload);
    } finally {
      setBypassBusy(false);
    }
  };

  const resetFilters = () => {
    setShiftFilter("");
    setSearch("");
    setStatusFilter("all");
    setDay(todayIso());
    setMonth(currentMonthValue());
  };

  if (!employee && !canViewTeam && !canMarkAttendance) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        Log in as an employee to see your attendance.
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Attendance</title>
      </Helmet>
      <div className="space-y-4" style={{ color: DASHBOARD.textDark }}>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-[#2C2C2A]">
              {canViewTeam ? "Attendance" : "My attendance"}
            </h1>
            <p className="mt-1 text-sm text-[#8A8A85]">
              {canMarkAttendance
                ? "Punch marks Present only. Super Admin, Admin, and Executive can edit any employee’s attendance anytime. Leave edits update balances."
                : "View your attendance. Punch marks you Present for the day."}
            </p>
          </div>
          <div className="flex rounded-xl border border-[#E8E8E4] bg-white p-1">
            {canViewTeam ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setTab("live");
                    setDay(todayIso());
                    setStatusFilter("all");
                  }}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
                    tab === "live" ? "bg-[#345952] text-white" : "text-[#4A4A48]"
                  }`}
                >
                  Live
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTab("daily");
                    setStatusFilter("all");
                  }}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
                    tab === "daily" ? "bg-[#345952] text-white" : "text-[#4A4A48]"
                  }`}
                >
                  Daily
                </button>
              </>
            ) : null}
            <button
              type="button"
              onClick={() => {
                setTab("month");
                setStatusFilter("all");
              }}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
                tab === "month" ? "bg-[#345952] text-white" : "text-[#4A4A48]"
              }`}
            >
              Month
            </button>
          </div>
        </div>

        {canDownloadReports ? (
          <AttendanceReportDownloads
            employees={verifiedEmployees.map((row) => ({
              emp_id: row.emp_id,
              emp_name: row.emp_name,
            }))}
          />
        ) : null}

        <div className="grid gap-4 lg:grid-cols-[240px_minmax(0,1fr)]">
          <aside className="rounded-2xl border border-[#E8E8E4] bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm font-semibold">Filters</p>
              <button
                type="button"
                onClick={resetFilters}
                className="text-xs font-medium text-[#345952]"
              >
                Reset
              </button>
            </div>
            {tab === "month" ? (
              <div className="mb-3">
                <CustomMonthPicker
                  label="Month"
                  value={month}
                  max={currentMonthValue()}
                  onChange={setMonth}
                />
              </div>
            ) : (
              <div className="mb-3">
                <CustomDatePicker
                  label="Date"
                  value={day}
                  max={todayIso()}
                  onChange={(iso) => {
                    setDay(iso);
                    setMonth(iso.slice(0, 7));
                  }}
                />
              </div>
            )}
            {canViewTeam ? (
              <label className="mb-3 block text-xs text-[#8A8A85]">
                Shift
                <AdminSoftSelect
                  className="mt-1 block w-full"
                  value={shiftFilter}
                  onChange={(e) => setShiftFilter(e.target.value)}
                >
                  <option value="">All shifts</option>
                  {shifts.map((shift) => (
                    <option key={shift} value={shift}>
                      {shift}
                    </option>
                  ))}
                </AdminSoftSelect>
              </label>
            ) : null}
            <p className="mt-4 text-xs leading-5 text-[#8A8A85]">
              {tab === "live"
                ? `Live view for ${prettyDate(day)}`
                : tab === "daily"
                  ? `Daily attendance · ${prettyDate(day)}`
                  : `Monthly dashboard · ${month}`}
            </p>
            {employee ? (
              <p className="mt-3 text-xs text-[#8A8A85]">
                Leave requests:{" "}
                <Link to="/dashboard/employee/leaves" className="font-medium text-[#345952] underline">
                  Leaves
                </Link>
              </p>
            ) : null}
          </aside>

          <section className="min-w-0 space-y-3">
            {tab === "live" ? (
              <div className="flex flex-wrap gap-2">
                <Chip
                  active={statusFilter === "all"}
                  label="All"
                  count={staffRows.length}
                  onClick={() => setStatusFilter("all")}
                />
                <Chip
                  active={statusFilter === "in"}
                  label="In"
                  count={liveCounts.inn}
                  onClick={() => setStatusFilter("in")}
                />
                <Chip
                  active={statusFilter === "out"}
                  label="Out"
                  count={liveCounts.out}
                  onClick={() => setStatusFilter("out")}
                />
                <Chip
                  active={statusFilter === "no_punch"}
                  label="No Punch In"
                  count={liveCounts.noPunch}
                  onClick={() => setStatusFilter("no_punch")}
                />
                <Chip
                  active={statusFilter === "late"}
                  label="Late"
                  count={liveCounts.late}
                  onClick={() => setStatusFilter("late")}
                />
                <Chip
                  active={statusFilter === "early"}
                  label="Early Leaving"
                  count={liveCounts.early}
                  onClick={() => setStatusFilter("early")}
                />
              </div>
            ) : null}

            {tab === "daily" ? (
              <div className="flex flex-wrap gap-2">
                <Chip
                  active={statusFilter === "all"}
                  label="All"
                  count={staffRows.length}
                  onClick={() => setStatusFilter("all")}
                />
                <Chip
                  active={statusFilter === "present"}
                  label="Present"
                  count={dailyCounts.present}
                  onClick={() => setStatusFilter("present")}
                />
                <Chip
                  active={statusFilter === "absent"}
                  label="Absent"
                  count={dailyCounts.absent}
                  onClick={() => setStatusFilter("absent")}
                />
                <Chip
                  active={statusFilter === "half_day"}
                  label="Half day"
                  count={dailyCounts.half_day}
                  onClick={() => setStatusFilter("half_day")}
                />
                <Chip
                  active={statusFilter === "on_leave"}
                  label="Leave"
                  count={dailyCounts.on_leave}
                  onClick={() => setStatusFilter("on_leave")}
                />
                <Chip
                  active={statusFilter === "unpaid"}
                  label="Unpaid"
                  count={dailyCounts.unpaid}
                  onClick={() => setStatusFilter("unpaid")}
                />
                <Chip
                  active={statusFilter === "holiday"}
                  label="Holiday"
                  count={dailyCounts.holiday}
                  onClick={() => setStatusFilter("holiday")}
                />
              </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-3">
              <div className="relative min-w-[220px] flex-1">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A85]">
                  ⌕
                </span>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search staff"
                  className="w-full rounded-xl border border-[#E8E8E4] bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-[#345952]/20"
                />
              </div>
              <p className="text-xs text-[#8A8A85]">
                Showing{" "}
                {tab === "month" ? monthAgg.length : filteredStaff.length} staff
              </p>
            </div>

            {error ? <p className="text-sm text-red-600">{error}</p> : null}
            {loading ? <p className="text-sm text-[#8A8A85]">Loading attendance…</p> : null}

            {tab === "month" && calendarEmpId ? (
              <div ref={monthCalendarRef} className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-sm font-semibold text-[#4A4A48]">
                    {canViewTeam
                      ? calendarEmpId === employee?.emp_id
                        ? "My monthly attendance"
                        : `${calendarName || "Employee"} · monthly attendance`
                      : "Monthly attendance"}
                  </h2>
                  {canViewTeam ? (
                    <select
                      value={calendarEmpId}
                      onChange={(e) => setMonthEmpId(Number(e.target.value))}
                      className="min-w-[220px] rounded-xl border border-[#E8E8E4] bg-white px-3 py-2 text-sm text-[#2C2C2A] outline-none focus:ring-2 focus:ring-[#345952]/20"
                      aria-label="Employee monthly attendance"
                    >
                      {monthAgg.map((row) => (
                        <option key={row.emp_id} value={row.emp_id}>
                          {row.name}
                          {row.role ? ` · ${row.role}` : ""}
                        </option>
                      ))}
                      {!monthAgg.some((row) => row.emp_id === calendarEmpId) ? (
                        <option value={calendarEmpId}>
                          {calendarName || `Employee ${calendarEmpId}`}
                        </option>
                      ) : null}
                    </select>
                  ) : null}
                </div>
                <AttendanceMonthCalendar
                  yearMonth={month}
                  onYearMonthChange={setMonth}
                  rows={calendarRows}
                />
              </div>
            ) : null}

            {tab === "month" ? (
              <div className="overflow-x-auto rounded-2xl border border-[#E8E8E4] bg-white">
                <table className="w-full min-w-[1100px] text-sm">
                  <thead>
                    <tr className="border-b border-[#E8E8E4] bg-[#FDFBF2] text-left text-xs uppercase tracking-wide text-[#8A8A85]">
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Role</th>
                      <th className="px-4 py-3">Present</th>
                      <th className="px-4 py-3">Absent</th>
                      <th className="px-4 py-3">Half day</th>
                      <th className="px-4 py-3">Week off</th>
                      <th className="px-4 py-3">Holiday</th>
                      <th className="px-4 py-3">Paid leave</th>
                      <th className="px-4 py-3">Unpaid</th>
                      <th className="px-4 py-3">Late</th>
                      <th className="px-4 py-3">Early</th>
                      {canMarkAttendance ? <th className="px-4 py-3">Action</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {monthAgg.length === 0 ? (
                      <tr>
                        <td
                          colSpan={canMarkAttendance ? 12 : 11}
                          className="px-4 py-8 text-center text-[#8A8A85]"
                        >
                          No attendance for this month
                        </td>
                      </tr>
                    ) : (
                      monthAgg.map((row) => (
                        <tr
                          key={row.emp_id}
                          className={`border-b border-[#F0F0EC] ${
                            canViewTeam && row.emp_id === calendarEmpId
                              ? "bg-[#F4FAF8]"
                              : ""
                          }`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#345952]/10 text-xs font-semibold text-[#345952]">
                                {initials(row.name)}
                              </div>
                              <div>
                                {canViewTeam ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setMonthEmpId(row.emp_id);
                                      monthCalendarRef.current?.scrollIntoView({
                                        behavior: "smooth",
                                        block: "start",
                                      });
                                    }}
                                    className="text-left font-medium text-[#345952] hover:underline"
                                  >
                                    {row.name}
                                  </button>
                                ) : (
                                  <p className="font-medium">{row.name}</p>
                                )}
                                <p className="text-xs text-[#8A8A85]">{row.shift || "—"}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-[#8A8A85]">{row.role || "—"}</td>
                          <td className="px-4 py-3">{row.present}</td>
                          <td className="px-4 py-3">{row.absent}</td>
                          <td className="px-4 py-3">{row.half_day}</td>
                          <td className="px-4 py-3">{row.weekend}</td>
                          <td className="px-4 py-3">{row.holiday}</td>
                          <td className="px-4 py-3">{row.paid_leave}</td>
                          <td className="px-4 py-3">{row.unpaid}</td>
                          <td className="px-4 py-3">{row.late}</td>
                          <td className="px-4 py-3">{row.early}</td>
                          {canMarkAttendance ? (
                            <td className="px-4 py-3">
                              <button
                                type="button"
                                onClick={() => openDrawer(row.emp_id, row.name)}
                                className="text-xs font-medium text-[#345952] underline"
                              >
                                Edit
                              </button>
                            </td>
                          ) : null}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : tab === "daily" ? (
              <div className="overflow-x-auto rounded-2xl border border-[#E8E8E4] bg-white">
                <table className="w-full min-w-[980px] text-sm">
                  <thead>
                    <tr className="border-b border-[#E8E8E4] bg-[#FDFBF2] text-left text-xs uppercase tracking-wide text-[#8A8A85]">
                      <th className="px-4 py-3">Staff</th>
                      <th className="px-4 py-3">First In</th>
                      <th className="px-4 py-3">Last Out</th>
                      <th className="px-4 py-3">Hours Worked</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Marked via</th>
                      {canMarkAttendance ? <th className="px-4 py-3">Action</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStaff.length === 0 ? (
                      <tr>
                        <td
                          colSpan={canMarkAttendance ? 7 : 6}
                          className="px-4 py-8 text-center text-[#8A8A85]"
                        >
                          No staff match these filters
                        </td>
                      </tr>
                    ) : (
                      filteredStaff.map(({ emp, row }) => {
                        const marked = attendanceMarkedHow(row);
                        return (
                        <tr key={emp.emp_id} className="border-b border-[#F0F0EC]">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="relative">
                                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#345952]/10 text-xs font-semibold text-[#345952]">
                                  {initials(emp.emp_name)}
                                </div>
                                <span
                                  className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white ${
                                    row?.status === "present" || row?.status === "wfh"
                                      ? "bg-emerald-500"
                                      : "bg-rose-400"
                                  }`}
                                />
                              </div>
                              <div>
                                <p className="font-medium">{emp.emp_name}</p>
                                <p className="text-xs text-[#8A8A85]">
                                  {emp.shift || "No shift"} · {emp.role || "Staff"}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">{clock(row?.punch_in_at)}</td>
                          <td className="px-4 py-3">{clock(row?.punch_out_at)}</td>
                          <td className="px-4 py-3">{formatWorked(row?.worked_minutes)}</td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${statusTone(
                                row?.status || "absent",
                              )}`}
                            >
                              {statusLabel(row?.status || "absent", row?.leaveType)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm text-[#4A4A48]">{marked.label}</p>
                            {marked.by ? (
                              <p className="text-xs text-[#8A8A85]">by {marked.by}</p>
                            ) : null}
                          </td>
                          {canMarkAttendance ? (
                            <td className="px-4 py-3">
                              <button
                                type="button"
                                onClick={() => openDrawer(emp.emp_id, emp.emp_name, row)}
                                className="text-xs font-medium text-[#345952] underline"
                              >
                                Edit
                              </button>
                            </td>
                          ) : null}
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="rounded-2xl border border-[#E8E8E4] bg-white">
                <div className="grid grid-cols-[1.4fr_0.8fr] border-b border-[#E8E8E4] bg-[#FDFBF2] px-4 py-3 text-xs uppercase tracking-wide text-[#8A8A85]">
                  <span>Staff</span>
                  <span>Status</span>
                </div>
                {filteredStaff.length === 0 ? (
                  <p className="px-4 py-8 text-center text-sm text-[#8A8A85]">
                    No staff match these filters
                  </p>
                ) : (
                  filteredStaff.map(({ emp, row }) => {
                    const bucket = liveBucket(row);
                    return (
                      <button
                        key={emp.emp_id}
                        type="button"
                        onClick={() =>
                          canMarkAttendance
                            ? openDrawer(emp.emp_id, emp.emp_name, row)
                            : undefined
                        }
                        className="flex w-full items-center justify-between gap-3 border-b border-[#F0F0EC] px-4 py-3 text-left hover:bg-[#FDFBF2]"
                      >
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#345952]/10 text-xs font-semibold text-[#345952]">
                              {initials(emp.emp_name)}
                            </div>
                            <span
                              className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white ${
                                bucket === "in" || bucket === "out"
                                  ? "bg-emerald-500"
                                  : "bg-rose-400"
                              }`}
                            />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{emp.emp_name}</p>
                            <p className="text-xs text-[#8A8A85]">
                              {emp.shift || "No shift"} · {emp.role || "Staff"}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
                              bucket === "in"
                                ? "bg-[#E8F3EF] text-[#345952]"
                                : bucket === "out"
                                  ? "bg-[#EAF2F8] text-[#3B6B9B]"
                                  : "bg-[#F8EAEA] text-[#9B3B3B]"
                            }`}
                          >
                            {bucket === "in"
                              ? "In"
                              : bucket === "out"
                                ? "Out"
                                : "No Punch In"}
                          </span>
                          <p className="mt-1 text-xs text-[#8A8A85]">
                            {row?.punch_in_at ? clock(row.punch_in_at) : "—"}
                          </p>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            )}

            {canMarkAttendance ? (
              <div className="rounded-2xl border border-[#E8E8E4] bg-white p-4">
                <button
                  type="button"
                  onClick={() => setAdminTools((v) => !v)}
                  className="text-sm font-semibold text-[#345952]"
                >
                  {adminTools ? "Hide" : "Show"} punch bypass and notebook tools
                </button>
                {adminTools ? (
                  <div className="mt-3 space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="text-xs text-[#8A8A85]">
                        {bypass?.active && bypass.until
                          ? `Punch reminder off until ${new Date(bypass.until).toLocaleString("en-IN")}`
                          : "Bypass the punch-in reminder for 24 hours if the machine is down."}
                      </p>
                      {bypass?.active ? (
                        <button
                          type="button"
                          disabled={bypassBusy}
                          onClick={() => void toggleBypass(false)}
                          className="rounded-xl border border-[#DDDDD8] px-3 py-2 text-xs"
                        >
                          Turn reminder on
                        </button>
                      ) : (
                        <AdminPrimaryButton
                          disabled={bypassBusy}
                          onClick={() => void toggleBypass(true)}
                        >
                          Bypass 24 hours
                        </AdminPrimaryButton>
                      )}
                    </div>
                    {isHrAdmin || canEditNotebook ? <PunchIntegration embedded /> : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </section>
        </div>
      </div>

      {drawer ? (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/35">
          <button
            type="button"
            className="h-full flex-1 cursor-default"
            aria-label="Close drawer backdrop"
            onClick={() => setDrawer(null)}
          />
          <aside className="flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-[#E8E8E4] px-5 py-4">
              <div>
                <p className="text-base font-semibold text-[#2C2C2A]">
                  {prettyDate(drawer.attendance_date)}
                </p>
                <p className="mt-0.5 text-sm text-[#8A8A85]">{drawer.emp_name}</p>
              </div>
              <button
                type="button"
                onClick={() => setDrawer(null)}
                className="rounded-lg px-2 py-1 text-[#8A8A85] hover:bg-[#F5F5F0]"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
              <CustomDatePicker
                label="Date"
                value={drawer.attendance_date}
                onChange={(iso) =>
                  setDrawer((prev) => (prev ? { ...prev, attendance_date: iso } : prev))
                }
              />

              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8A8A85]">
                  Attendance
                </p>
                <div className="flex flex-wrap gap-2">
                  <StatusPill
                    active={drawer.status === "absent"}
                    label="Absent"
                    tone="bg-[#F8EAEA] text-[#9B3B3B]"
                    onClick={() => setDrawer((p) => (p ? { ...p, status: "absent" } : p))}
                  />
                  <StatusPill
                    active={drawer.status === "half_day"}
                    label="Half day"
                    tone="bg-[#EAF2F8] text-[#3B6B9B]"
                    onClick={() =>
                      setDrawer((p) =>
                        p
                          ? {
                              ...p,
                              status: "half_day",
                              leave_type_code: ["PL", "CL", "UL"].includes(p.leave_type_code)
                                ? p.leave_type_code
                                : "CL",
                            }
                          : p,
                      )
                    }
                  />
                  <StatusPill
                    active={drawer.status === "present"}
                    label="Present"
                    tone="bg-[#E8F3EF] text-[#345952]"
                    onClick={() => setDrawer((p) => (p ? { ...p, status: "present" } : p))}
                  />
                  <StatusPill
                    active={drawer.status === "weekend"}
                    label="Week off"
                    tone="bg-[#F5F5F0] text-[#8A8A85]"
                    onClick={() =>
                      setDrawer((p) =>
                        p
                          ? {
                              ...p,
                              status: "weekend",
                              notes:
                                p.notes?.trim() && p.status === "weekend"
                                  ? p.notes
                                  : p.notes?.trim() || "Custom week off",
                            }
                          : p,
                      )
                    }
                  />
                  <StatusPill
                    active={drawer.status === "holiday" && drawer.leave_type_code !== "HOL"}
                    label="Holiday"
                    tone="bg-[#F0ECF8] text-[#5B4A8A]"
                    onClick={() =>
                      setDrawer((p) =>
                        p ? { ...p, status: "holiday", leave_type_code: "PL" } : p,
                      )
                    }
                  />
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8A8A85]">
                  Leaves
                </p>
                <div className="flex flex-wrap gap-2">
                  <StatusPill
                    active={drawer.status === "on_leave" && drawer.leave_type_code === "PL"}
                    label="Privilege leave"
                    tone="bg-[#E8F3EF] text-[#345952]"
                    onClick={() =>
                      setDrawer((p) =>
                        p ? { ...p, status: "on_leave", leave_type_code: "PL" } : p,
                      )
                    }
                  />
                  <StatusPill
                    active={drawer.status === "on_leave" && drawer.leave_type_code === "CL"}
                    label="Casual leave"
                    tone="bg-[#F3EEE8] text-[#8A6A48]"
                    onClick={() =>
                      setDrawer((p) =>
                        p ? { ...p, status: "on_leave", leave_type_code: "CL" } : p,
                      )
                    }
                  />
                  <StatusPill
                    active={drawer.status === "half_day"}
                    label="Half day leave"
                    tone="bg-[#EAF2F8] text-[#3B6B9B]"
                    onClick={() =>
                      setDrawer((p) =>
                        p ? { ...p, status: "half_day", leave_type_code: "CL" } : p,
                      )
                    }
                  />
                  <StatusPill
                    active={drawer.status === "unpaid"}
                    label="Unpaid leave"
                    tone="bg-[#F8EAEA] text-[#9B3B3B]"
                    onClick={() =>
                      setDrawer((p) =>
                        p ? { ...p, status: "unpaid", leave_type_code: "UL" } : p,
                      )
                    }
                  />
                  <StatusPill
                    active={drawer.status === "holiday" && drawer.leave_type_code === "HOL"}
                    label="Holiday quota"
                    tone="bg-[#F0ECF8] text-[#5B4A8A]"
                    onClick={() =>
                      setDrawer((p) =>
                        p ? { ...p, status: "holiday", leave_type_code: "HOL" } : p,
                      )
                    }
                  />
                </div>
              </div>

              {drawer.status === "half_day" ? (
                <label className="block text-xs text-[#8A8A85]">
                  Half day leave type
                  <AdminSoftSelect
                    className="mt-1 block w-full"
                    value={drawer.leave_type_code}
                    onChange={(e) =>
                      setDrawer((p) => (p ? { ...p, leave_type_code: e.target.value } : p))
                    }
                  >
                    <option value="CL">Casual Leave 0.5</option>
                    <option value="PL">Privilege Leave 0.5</option>
                    <option value="UL">Unpaid 0.5</option>
                  </AdminSoftSelect>
                </label>
              ) : null}

              <label className="block text-xs text-[#8A8A85]">
                Add note
                <textarea
                  value={drawer.notes}
                  onChange={(e) =>
                    setDrawer((p) => (p ? { ...p, notes: e.target.value } : p))
                  }
                  rows={3}
                  className="mt-1 w-full rounded-xl border border-[#E8E8E4] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#345952]/20"
                  placeholder="Reason for manual mark or edit"
                />
              </label>

              {canEditBalances ? (
                <div className="space-y-2 border-t border-[#E8E8E4] pt-4">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#8A8A85]">
                      Leave balances
                    </p>
                    {balancesLoading ? (
                      <span className="text-[11px] text-[#8A8A85]">Loading…</span>
                    ) : null}
                  </div>
                  <p className="text-[11px] leading-4 text-[#8A8A85]">
                    Increase or decrease remaining PL, CL, and Holiday. Unpaid has no limit — set
                    how many unpaid days have been used.
                  </p>
                  <BalanceStepper
                    label="Privilege leave"
                    hint="Remaining days"
                    value={balanceDraft.pl}
                    onChange={(pl) => setBalanceDraft((p) => ({ ...p, pl }))}
                  />
                  <BalanceStepper
                    label="Casual leave"
                    hint="Remaining days"
                    value={balanceDraft.cl}
                    onChange={(cl) => setBalanceDraft((p) => ({ ...p, cl }))}
                  />
                  <BalanceStepper
                    label="Holiday quota"
                    hint="Remaining days"
                    value={balanceDraft.hol}
                    onChange={(hol) => setBalanceDraft((p) => ({ ...p, hol }))}
                  />
                  <BalanceStepper
                    label="Unpaid leave"
                    hint="Used days · no limit"
                    value={balanceDraft.ul}
                    onChange={(ul) => setBalanceDraft((p) => ({ ...p, ul }))}
                    step={0.5}
                  />
                </div>
              ) : null}

              {saveMsg ? <p className="text-sm text-emerald-700">{saveMsg}</p> : null}
              {saveErr ? <p className="text-sm text-red-600">{saveErr}</p> : null}
            </div>
            <div className="space-y-2 border-t border-[#E8E8E4] px-5 py-4">
              <AdminPrimaryButton
                className="w-full justify-center"
                disabled={saving}
                onClick={() => void saveDrawer()}
              >
                {saving ? "Saving…" : "Save attendance"}
              </AdminPrimaryButton>
              {canEditBalances ? (
                <button
                  type="button"
                  disabled={savingBalances || balancesLoading}
                  onClick={() => void saveBalances()}
                  className="w-full rounded-xl border border-[#345952] px-4 py-2.5 text-xs font-semibold text-[#345952] transition hover:bg-[#E8F0EE] disabled:opacity-50"
                >
                  {savingBalances ? "Saving balances…" : "Save leave balances"}
                </button>
              ) : null}
            </div>
          </aside>
        </div>
      ) : null}
    </>
  );
};

export default AttendanceReport;
